import React, {
  Children,
  ReactElement,
  ReactNode,
  isValidElement,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Button,
  ButtonGroup,
  Checkbox,
  CheckboxGroup,
  Stack,
} from "@chakra-ui/react";
import { FiHelpCircle } from "react-icons/fi";
import {
  TaskContent,
  TaskEvent,
  TaskStatus,
  TaskStatusProps,
  TaskWrapper,
  useFlash,
  useTask,
  useTrials,
} from "./Task";
import { shuffle } from "lodash";

export function useChoices(
  children: ReactNode
): [{ children: ReactNode }[], string[]] {
  return useMemo(() => {
    const choices = shuffle(
      Children.toArray(children)
        .filter(isValidElement)
        .map((child) => (child as ReactElement).props)
        .filter(({ mdxType }) => mdxType === "Choice")
    );
    return [
      choices,
      choices.reduce(
        (acc: string[], choice, index) =>
          choice.isCorrect ? [...acc, index.toString()] : acc,
        []
      ),
    ];
  }, [children]);
}

type MultipleChoiceProps = {
  children: ReactNode;
  title?: string;
  taskId: string;
} & Pick<TaskStatusProps, "successMessage" | "failureMessage">;

export function MultipleChoice({
  children,
  taskId,
  title = "Quiz!",
  ...taskStatusProps
}: MultipleChoiceProps) {
  const [shuffledChoices, correctChoices] = useChoices(children);

  const { isTaskComplete, saveTask, savingTask } = useTask(taskId);

  const [values, setValues] = useState<string[]>([]);
  const [submitted, setSubmitted] = useState(false);

  const { trialsRemaining, resetTrials } = useTrials();

  const isCorrect = useMemo(
    () =>
      values.length === correctChoices.length &&
      correctChoices.every((value) => values.includes(value)),
    [values, correctChoices]
  );

  const isSubmittedAndCorrect = useMemo(
    () => submitted && isCorrect,
    [submitted, isCorrect]
  );

  useEffect(() => {
    if (isTaskComplete) {
      setValues(correctChoices);
      setSubmitted(true);
    }
  }, [isTaskComplete, correctChoices]);

  const flash = useFlash();

  return (
    <TaskWrapper
      taskId={taskId}
      title={title}
      icon={FiHelpCircle}
      isComplete={isSubmittedAndCorrect}
      {...flash}
    >
      <CheckboxGroup
        value={values}
        onChange={(nextValues) => {
          setValues(nextValues.map(String));
          setSubmitted(false);
        }}
      >
        <Stack align="flex-start">
          {shuffledChoices.map((choice, index) => (
            <Checkbox
              colorScheme={isSubmittedAndCorrect ? "green" : "indigo"}
              key={index}
              value={index.toString()}
            >
              <TaskContent>{choice.children}</TaskContent>
            </Checkbox>
          ))}
        </Stack>
      </CheckboxGroup>
      {!isSubmittedAndCorrect && (
        <ButtonGroup mt="3">
          <Button
            isLoading={savingTask}
            isDisabled={!values.length}
            onClick={() => {
              setSubmitted(true);
              resetTrials(isCorrect);
              saveTask(isCorrect ? new Date() : null);
            }}
          >
            Submit
          </Button>
          {trialsRemaining <= 0 && (
            <Button
              isDisabled={savingTask}
              colorScheme="green"
              onClick={() => {
                setSubmitted(true);
                resetTrials();
                setValues(correctChoices);
                saveTask(new Date());
              }}
            >
              Show solution
            </Button>
          )}
        </ButtonGroup>
      )}
      {submitted && (
        <TaskStatus
          isCorrect={isCorrect}
          trialsRemaining={trialsRemaining}
          {...taskStatusProps}
        />
      )}
      <TaskEvent isComplete={isSubmittedAndCorrect} taskId={taskId} />
    </TaskWrapper>
  );
}
